// Chrome/Edge/Android evaluate PWA installability from whatever the current
// <link rel="manifest"> points to, not from a fixed file — so a kiosk tablet
// that installs from /kiosk/<tenant-slug> needs a manifest whose start_url
// bakes in that exact path, otherwise "Add to Home Screen" would relaunch at
// the bare /kiosk landing page (no slug) instead of that site's kiosk.
// We build the manifest object in JS per surface and swap the manifest link
// to a Blob URL, so one static manifest.json (used as a same-origin default
// for search engines / direct hits) can be overridden per-tenant at runtime.
// A manifest loaded from a blob: URL has no address to resolve relative paths against, so Chrome ignores a relative start_url, scope or
// icon src ("URL is invalid") and the app can't be installed. Everything in it is therefore made absolute with the site's own address.
const abs = (path) => new URL(path, window.location.origin).href;
const ICONS = () => [
  { src: abs("/icon-192.png"), sizes: "192x192", type: "image/png", purpose: "any" },
  { src: abs("/icon-512.png"), sizes: "512x512", type: "image/png", purpose: "any" },
];
const SPLASH = "#f4f7f5";      // the site starts in light mode

function surfaceFor(pathname) {
  if (pathname.startsWith("/kiosk")) return "kiosk";
  if (pathname.startsWith("/family") || pathname.startsWith("/embed")) return "family";
  return "admin";
}

function manifestFor(surface, pathname) {
  if (surface === "kiosk") {
    return {
      name: "Out of School Hub Kiosk",
      short_name: "OOSH Kiosk",
      description: "Staff sign-in/out, child check-in and daily roll kiosk.",
      start_url: abs(pathname),
      scope: abs("/kiosk"),
      display: "standalone",
      orientation: "any",       // the tablet can be turned either way; locking it to landscape left it stuck
      background_color: SPLASH,
      theme_color: SPLASH,
      icons: ICONS(),
    };
  }
  if (surface === "family") {
    return {
      name: "Out of School Hub Family",
      short_name: "OOSH Family",
      description: "Book sessions, pay invoices and keep your children's details up to date.",
      start_url: abs("/family"),
      scope: abs("/"),
      display: "standalone",
      background_color: SPLASH,
      theme_color: SPLASH,
      icons: ICONS(),
    };
  }
  return {
    name: "Out of School Hub",
    short_name: "OOSH Admin",
    description: "Manage bookings, families, finance and staff for your programme.",
    start_url: abs("/"),
    scope: abs("/"),
    display: "standalone",
    background_color: SPLASH,
    theme_color: SPLASH,
    icons: ICONS(),
  };
}

let lastKey = null;
let lastBlobUrl = null;

export function applyDynamicManifest(pathname = window.location.pathname) {
  const surface = surfaceFor(pathname);
  const manifest = manifestFor(surface, pathname);
  // Key on start_url (not just surface) so a kiosk installed for one tenant
  // slug still gets a fresh manifest if the same tablet later browses a
  // different tenant's kiosk link -- but skip regenerating (and leaking) a
  // Blob URL when nothing has actually changed.
  const key = manifest.start_url;
  if (key === lastKey) return;
  const link = document.querySelector('link[rel="manifest"]');
  if (!link) return;
  const blob = new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" });
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  if (lastBlobUrl) URL.revokeObjectURL(lastBlobUrl);
  lastBlobUrl = url;
  lastKey = key;

  const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (appleTitle) appleTitle.setAttribute("content", manifest.short_name);
}
